import { PhoneNumber } from 'interfaces/phone-number'
import { UserWithAppleId } from 'interfaces/user-with-apple-id'
import { Badge, Button, Card, Table } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { ID } from 'utils/types'
import moment from 'moment'
import useCreateCancelTicket from 'hooks/use-create-cancel-ticket'
import useGetPartnerToken from 'hooks/use-get-partner-token'
import toast from 'react-hot-toast'
import { shouldBlockGhostMessages } from 'utils/ghost-access'

interface OrganizationPhoneNumbersTableProps {
  orgId: string
  usersWithAppleIdsData?: {
    data: {
      users: UserWithAppleId[]
    }
  }
  phoneNumbers?: PhoneNumber[]
  isPhoneNumbersFetching?: boolean
  redactMessageHistory?: boolean
  customerProductType?: string
}
const OrganizationPhoneNumbersTable = ({
  orgId,
  usersWithAppleIdsData,
  phoneNumbers,
  isPhoneNumbersFetching,
  redactMessageHistory,
  customerProductType,
}: OrganizationPhoneNumbersTableProps) => {
  const usersWithAppleIds = usersWithAppleIdsData?.data?.users
  const createCancelTicket = useCreateCancelTicket()
  const { data: tokenData } = useGetPartnerToken(orgId)
  const isCli = (tokenData?.data || []).some((i) => i.tier === 'cli')

  const handleCancelBlueNumber = (userId: ID) => {
    if (!userId) {
      toast.error('User ID not found', {
        style: {
          background: '#343a40',
          color: '#fff',
          border: '1px solid #495057',
        },
      })
      return
    }

    if (
      window.confirm(
        'Are you sure you want to cancel this blue number? This will create a cancel ticket in HubSpot.'
      )
    ) {
      createCancelTicket.mutate({ user_id: String(userId) })
    }
  }

  // Create a component for each row to handle data properly
  const PhoneNumberRow = ({
    phoneNumber,
    index,
  }: {
    phoneNumber: PhoneNumber
    index: number
  }) => {
    const userWithAppleId = usersWithAppleIds?.find(
      (user: UserWithAppleId) => user.id === phoneNumber.user_id
    )
    const name = userWithAppleId?.name || 'AVAILABLE'
    const appleIdData = userWithAppleId?.apple_id
    const isLinqBlue = !!appleIdData
    const service = isLinqBlue ? 'Linq Blue' : 'Linq One'
    const responseRate = isLinqBlue
      ? appleIdData?.response_rate || 'N/A'
      : 'N/A'
    const createdAt =
      isLinqBlue && appleIdData?.created_at
        ? moment(appleIdData.created_at).format('MMM DD, YYYY')
        : phoneNumber.created_at
        ? moment(phoneNumber.created_at).format('MMM DD, YYYY')
        : 'N/A'

    return (
      <tr key={index}>
        <td>{index + 1}</td>
        <td>
          {name !== 'AVAILABLE' ? (
            <Link
              to={`/user/${userWithAppleId?.id}`}
              style={{
                color: '#4da6ff',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {name}
            </Link>
          ) : (
            name
          )}
        </td>
        <td>
          {isLinqBlue && appleIdData ? (
            <span style={{ color: '#4da6ff' }}>{appleIdData.phone_number}</span>
          ) : (
            userWithAppleId?.work_phone_number || phoneNumber.number
          )}
        </td>

        <td style={{ color: isLinqBlue ? '#007bff' : 'inherit' }}>{service}</td>
        <td>{responseRate === 'N/A' ? responseRate : `${responseRate}%`}</td>
        <td>{createdAt}</td>
        <td>
          {isLinqBlue && (
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                handleCancelBlueNumber(phoneNumber.user_id)
              }}
              disabled={createCancelTicket.isLoading}
            >
              {createCancelTicket.isLoading ? 'Creating...' : 'Cancel'}
            </Button>
          )}
        </td>
      </tr>
    )
  }
  // Get user IDs that are already in phoneNumbers to avoid duplicates
  const phoneNumberUserIds = new Set(
    phoneNumbers?.map((pn) => String(pn.user_id)) || []
  )

  // Filter usersWithAppleIds to get users not already in phoneNumbers and who have a phone number
  const additionalUsers =
    usersWithAppleIds?.filter(
      (user) =>
        !phoneNumberUserIds.has(String(user.id)) &&
        (user.apple_id?.phone_number ||
          user.work_phone_number ||
          user.phone_number)
    ) || []

  // Component to render additional users from usersWithAppleIds
  const AdditionalUserRow = ({
    user,
    index,
  }: {
    user: UserWithAppleId
    index: number
  }) => {
    const appleIdData = user.apple_id
    const isLinqBlue = !!appleIdData
    const service = isLinqBlue ? 'Linq Blue' : 'Linq One'
    const responseRate = isLinqBlue
      ? appleIdData?.response_rate || 'N/A'
      : 'N/A'
    const createdAt =
      isLinqBlue && appleIdData?.created_at
        ? moment(appleIdData.created_at).format('MMM DD, YYYY')
        : user.created_at
        ? moment(user.created_at).format('MMM DD, YYYY')
        : 'N/A'

    return (
      <tr key={`additional-${user.id}`}>
        <td>{index + 1}</td>
        <td>
          <Link
            to={`/user/${user.uuid}`}
            style={{
              color: '#4da6ff',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            {user.name || 'Unknown'}
          </Link>
        </td>
        <td>
          {isLinqBlue && appleIdData ? (
            <span style={{ color: '#4da6ff' }}>{appleIdData.phone_number}</span>
          ) : (
            user.work_phone_number || user.phone_number || 'N/A'
          )}
        </td>
        <td style={{ color: isLinqBlue ? '#007bff' : 'inherit' }}>{service}</td>
        <td>{responseRate === 'N/A' ? responseRate : `${responseRate}%`}</td>
        <td>{createdAt}</td>
        <td>
          {isLinqBlue && (
            <Button
              variant="danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                handleCancelBlueNumber(user.id)
              }}
              disabled={createCancelTicket.isLoading}
            >
              {createCancelTicket.isLoading ? 'Creating...' : 'Cancel'}
            </Button>
          )}
        </td>
      </tr>
    )
  }

  return (
    <Card className="mt-4">
      {isPhoneNumbersFetching ? (
        <div className="m-2">Loading...</div>
      ) : (
        <>
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0">
              Organization Phone Numbers - Linq Phone
              {isCli && (
                <Badge
                  variant="info"
                  className="ms-2"
                  style={{ fontSize: '0.7rem' }}
                >
                  Shared Line
                </Badge>
              )}
            </h4>
            {shouldBlockGhostMessages(
              redactMessageHistory,
              customerProductType
            ) ? (
              <Button
                variant="primary"
                size="sm"
                disabled
                title="Message history is redacted for this organization"
              >
                Organization User Chats
              </Button>
            ) : (
              <Link to={`/organizations/${orgId}/chat_messages`}>
                <Button variant="primary" size="sm">
                  Organization User Chats
                </Button>
              </Link>
            )}
          </Card.Header>
          <Card.Body>
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                border: '1px solid #495057',
                borderRadius: '0.375rem',
              }}
            >
              <Table
                striped
                bordered
                hover
                variant="dark"
                className="mb-0"
                style={{ borderRadius: '0' }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#343a40',
                  }}
                >
                  <tr>
                    <th style={{ borderTop: 'none' }}>#</th>
                    <th style={{ borderTop: 'none' }}>User</th>
                    <th style={{ borderTop: 'none' }}>Phone Number</th>
                    <th style={{ borderTop: 'none' }}>Service</th>
                    <th style={{ borderTop: 'none' }}>Response Rate</th>
                    <th style={{ borderTop: 'none' }}>Created At</th>
                    <th style={{ borderTop: 'none' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {/* First render all entries from phoneNumbers */}
                  {phoneNumbers &&
                    usersWithAppleIds &&
                    phoneNumbers.map((phoneNumber, index) => (
                      <PhoneNumberRow
                        key={`phone-${index}`}
                        phoneNumber={phoneNumber}
                        index={index}
                      />
                    ))}
                  {/* Then render additional users from usersWithAppleIds not already in phoneNumbers */}
                  {usersWithAppleIds &&
                    additionalUsers.map((user, index) => (
                      <AdditionalUserRow
                        key={`user-${index}`}
                        user={user}
                        index={(phoneNumbers?.length || 0) + index}
                      />
                    ))}
                  {/* CLI orgs don't own direct numbers — they're on a shared line */}
                  {isCli &&
                    (phoneNumbers?.length || 0) === 0 &&
                    additionalUsers.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          This organization is on a shared line. See helix for
                          shared-line details.
                        </td>
                      </tr>
                    )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </>
      )}
    </Card>
  )
}

export default OrganizationPhoneNumbersTable
